import { useRef, useState, useCallback, useEffect } from 'react';
import { Paperclip, X, Loader2 } from 'lucide-react';
import { api, type AttachmentRef } from '../lib/api.js';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export interface UploadedImage {
  id: string;
  previewUrl: string;
  filename: string;
}

interface Props {
  /** Массив загруженных изображений (controlled). */
  images: UploadedImage[];
  /** Callback при изменении списка изображений. */
  onChange: (images: UploadedImage[]) => void;
  /** Отключить загрузку (например, при sending). */
  disabled?: boolean;
  /** Ref на контейнер для отслеживания drag&drop. */
  dropZoneRef?: React.RefObject<HTMLElement | null>;
}

export function ImageUploader({ images, onChange, disabled, dropZoneRef }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (disabled || uploading) return;

      const validFiles = files.filter((f) => {
        if (!ALLOWED_TYPES.includes(f.type)) {
          setError(`Неподдерживаемый формат: ${f.type}`);
          return false;
        }
        if (f.size > MAX_FILE_SIZE) {
          setError(`Файл слишком большой: ${(f.size / 1024 / 1024).toFixed(1)} MB (макс. 10 MB)`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) return;

      setUploading(true);
      setError(null);

      try {
        const newImages: UploadedImage[] = [];
        for (const file of validFiles) {
          const ref: AttachmentRef = await api.uploadFile(file);
          newImages.push({
            id: ref.id,
            previewUrl: URL.createObjectURL(file),
            filename: file.name,
          });
        }
        onChange([...images, ...newImages]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки');
      } finally {
        setUploading(false);
      }
    },
    [disabled, uploading, images, onChange],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length > 0) {
        void uploadFiles(files);
      }
      // Сбросить input, чтобы повторный выбор того же файла сработал.
      e.target.value = '';
    },
    [uploadFiles],
  );

  const removeImage = useCallback(
    (id: string) => {
      const img = images.find((i) => i.id === id);
      if (img) {
        URL.revokeObjectURL(img.previewUrl);
      }
      onChange(images.filter((i) => i.id !== id));
    },
    [images, onChange],
  );

  // Paste handler — глобальный для документа, когда фокус в textarea.
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (disabled) return;
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (const item of items) {
        if (item.kind === 'file' && ALLOWED_TYPES.includes(item.type)) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        void uploadFiles(imageFiles);
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [disabled, uploadFiles]);

  // Drag & drop handler для указанной зоны.
  useEffect(() => {
    const zone = dropZoneRef?.current;
    if (!zone) return;

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setDragOver(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      if (disabled) return;
      const files = Array.from(e.dataTransfer?.files ?? []).filter((f) =>
        ALLOWED_TYPES.includes(f.type),
      );
      if (files.length > 0) {
        void uploadFiles(files);
      }
    };

    zone.addEventListener('dragover', handleDragOver);
    zone.addEventListener('dragleave', handleDragLeave);
    zone.addEventListener('drop', handleDrop);

    return () => {
      zone.removeEventListener('dragover', handleDragOver);
      zone.removeEventListener('dragleave', handleDragLeave);
      zone.removeEventListener('drop', handleDrop);
    };
  }, [dropZoneRef, disabled, uploadFiles]);

  // Cleanup blob URLs on unmount.
  useEffect(() => {
    return () => {
      images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-2">
      {/* Превью загруженных изображений */}
      {images.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {images.map((img) => (
            <div key={img.id} className="relative group">
              <img
                src={img.previewUrl}
                alt={img.filename}
                className="w-16 h-16 object-cover rounded-lg border border-neutral-700"
              />
              <button
                type="button"
                onClick={() => removeImage(img.id)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label={`Удалить ${img.filename}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Ошибка */}
      {error && (
        <div className="text-xs text-red-400">{error}</div>
      )}

      {/* Drag overlay indicator */}
      {dragOver && (
        <div className="absolute inset-0 bg-blue-500/20 border-2 border-dashed border-blue-400 rounded-xl flex items-center justify-center z-10 pointer-events-none">
          <span className="text-blue-200 text-sm">Отпустите для загрузки</span>
        </div>
      )}

      {/* Скрытый input */}
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      {/* Кнопка прикрепления */}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        className="p-2 rounded-lg text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800 disabled:opacity-50 transition-colors"
        title="Прикрепить изображение"
        aria-label="Прикрепить изображение"
      >
        {uploading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Paperclip className="w-5 h-5" />
        )}
      </button>
    </div>
  );
}
